import React from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  InputAdornment,
  Box,
  Typography,
  IconButton,
  Paper
} from '@mui/material';
import {
  Instagram,
  Facebook,
  Twitter,
  YouTube,
  LinkedIn,
  Language,
  Add,
  Delete,
  MusicNote
} from '@mui/icons-material';
import { FormSection, TextField as DesignTextField } from '../../../../design-system/components/forms';
import { TextField } from '@mui/material';
import { Button } from '../../../../design-system/components/core';

const VendorSocialMediaForm = ({ formik }) => {
  const socialPlatforms = [
    { name: 'instagram', icon: Instagram, label: 'Instagram', placeholder: '@username' },
    { name: 'tiktok', icon: MusicNote, label: 'TikTok', placeholder: '@username' },
    { name: 'facebook', icon: Facebook, label: 'Facebook', placeholder: 'facebook.com/page' },
    { name: 'twitter', icon: Twitter, label: 'Twitter', placeholder: '@handle' },
    { name: 'youtube', icon: YouTube, label: 'YouTube', placeholder: 'youtube.com/channel' },
    { name: 'linkedin', icon: LinkedIn, label: 'LinkedIn', placeholder: 'linkedin.com/in/profile' }
  ];

  const handleSocialMediaChange = (platform, field, value) => {
    const currentSocialMedia = formik.values.socialMedia || {};
    const platformData = currentSocialMedia[platform] || {};
    
    formik.setFieldValue(`socialMedia.${platform}`, {
      ...platformData,
      [field]: value
    });
  };

  const addCustomPlatform = () => {
    const currentCustomPlatforms = formik.values.customPlatforms || [];
    formik.setFieldValue('customPlatforms', [
      ...currentCustomPlatforms,
      { name: '', url: '', followers: '' }
    ]);
  };

  const removeCustomPlatform = (index) => {
    const currentCustomPlatforms = formik.values.customPlatforms || [];
    const updated = currentCustomPlatforms.filter((_, i) => i !== index);
    formik.setFieldValue('customPlatforms', updated);
  };

  const updateCustomPlatform = (index, field, value) => {
    const currentCustomPlatforms = formik.values.customPlatforms || [];
    const updated = [...currentCustomPlatforms];
    updated[index] = { ...updated[index], [field]: value };
    formik.setFieldValue('customPlatforms', updated);
  };

  return (
    <FormSection 
      title="Social Media & Online Presence" 
      subtitle="Add social media profiles and follower counts"
    >
      <Grid container spacing={3}>
        {/* Main social platforms */}
        {socialPlatforms.map((platform) => {
          const Icon = platform.icon;
          const socialData = formik.values.socialMedia?.[platform.name] || {};
          
          return (
            <Grid item xs={12} key={platform.name}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Icon color="action" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">{platform.label}</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Profile URL/Username"
                      placeholder={platform.placeholder}
                      value={socialData.handle || ''}
                      onChange={(e) => handleSocialMediaChange(platform.name, 'handle', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={platform.name === 'youtube' ? "Subscribers Count" : platform.name === 'linkedin' ? "Connections" : "Followers Count"}
                      type="number"
                      value={
                        platform.name === 'youtube' ? socialData.subscribers || '' : 
                        platform.name === 'linkedin' ? socialData.connections || '' : 
                        socialData.followers || ''
                      }
                      onChange={(e) => {
                        const field = platform.name === 'youtube' ? 'subscribers' : 
                                     platform.name === 'linkedin' ? 'connections' : 
                                     'followers';
                        handleSocialMediaChange(platform.name, field, Number(e.target.value) || '');
                      }}
                      variant="outlined"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">
                          {platform.name === 'youtube' ? 'subscribers' : 
                           platform.name === 'linkedin' ? 'connections' : 
                           'followers'}
                        </InputAdornment>
                      }}
                    />
                  </Grid>
                  {(platform.name === 'instagram' || platform.name === 'tiktok') && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Average Engagement Rate"
                        type="number"
                        value={socialData.engagementRate || ''}
                        onChange={(e) => handleSocialMediaChange(platform.name, 'engagementRate', Number(e.target.value) || '')}
                        variant="outlined"
                        helperText="Enter as percentage (e.g., 3.5 for 3.5%)"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }}
                      />
                    </Grid>
                  )}
                  {platform.name === 'youtube' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Average Views per Video"
                        type="number"
                        value={socialData.avgViews || ''}
                        onChange={(e) => handleSocialMediaChange(platform.name, 'avgViews', Number(e.target.value) || '')}
                        variant="outlined"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">views</InputAdornment>
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          );
        })}

        {/* Custom platforms */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1">Other Platforms</Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Add />}
              onClick={addCustomPlatform}
            >
              Add Platform
            </Button>
          </Box>

          {(formik.values.customPlatforms || []).map((platform, index) => (
            <Paper 
              key={index} 
              elevation={0} 
              sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="start">
                <Grid container spacing={2} sx={{ flex: 1 }}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Platform Name"
                      value={platform.name || ''}
                      onChange={(e) => updateCustomPlatform(index, 'name', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Profile URL"
                      value={platform.url || ''}
                      onChange={(e) => updateCustomPlatform(index, 'url', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Followers Count"
                      type="number"
                      value={platform.followers || ''}
                      onChange={(e) => updateCustomPlatform(index, 'followers', Number(e.target.value) || '')}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
                <IconButton
                  color="error"
                  onClick={() => removeCustomPlatform(index)}
                  sx={{ ml: 2 }}
                >
                  <Delete />
                </IconButton>
              </Box>
            </Paper>
          ))}
        </Grid>

        {/* Content specializations */}
        <Grid item xs={12}>
          <DesignTextField
            fullWidth
            multiline
            rows={3}
            name="contentSpecializations"
            label="Content Specializations"
            placeholder="E.g., Fashion, Travel, Food, Technology, Lifestyle"
            value={formik.values.contentSpecializations || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.contentSpecializations && Boolean(formik.errors.contentSpecializations)}
            helperText={formik.touched.contentSpecializations && formik.errors.contentSpecializations}
          />
        </Grid>

        {/* Target audience */}
        <Grid item xs={12}>
          <DesignTextField
            fullWidth
            multiline
            rows={3}
            name="targetAudience"
            label="Target Audience Description"
            placeholder="Describe the influencer's typical audience demographics and interests"
            value={formik.values.targetAudience || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.targetAudience && Boolean(formik.errors.targetAudience)}
            helperText={formik.touched.targetAudience && formik.errors.targetAudience}
          />
        </Grid>
      </Grid>
    </FormSection>
  );
};

VendorSocialMediaForm.propTypes = {
  formik: PropTypes.object.isRequired
};

export default VendorSocialMediaForm;